import React, { useMemo } from 'react';
import { IconButton, Stack } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';

const CENTRAL_TIMEZONE = 'America/Chicago';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    timeZone: CENTRAL_TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const SECTION_CONFIG = {
  triggerExecutions: {
    title: 'Trigger Execution',
    columns: [
      { accessorKey: 'executedAt', header: 'Executed', accessorFn: (row) => formatDateTime(row.executedAt) },
      { accessorKey: 'programName', header: 'Program' },
      { accessorKey: 'templateName', header: 'Template' },
      { accessorKey: 'scope', header: 'Scope' },
      { accessorKey: 'status', header: 'Status' },
    ],
  },
  dpeStatusChanges: {
    title: 'DPE Status Changes',
    columns: [
      { accessorKey: 'changedAt', header: 'Changed', accessorFn: (row) => formatDateTime(row.changedAt) },
      { accessorKey: 'programName', header: 'Program' },
      { accessorKey: 'bac', header: 'BAC' },
      { accessorKey: 'dealerName', header: 'Dealer' },
      { accessorKey: 'previousStatus', header: 'Previous' },
      { accessorKey: 'newStatus', header: 'New' },
    ],
  },
  appsheetStatusChanges: {
    title: 'AppSheet Status Changes',
    columns: [
      { accessorKey: 'changedAt', header: 'Changed', accessorFn: (row) => formatDateTime(row.changedAt) },
      { accessorKey: 'programName', header: 'Program' },
      { accessorKey: 'rowKey', header: 'Row Key' },
      { accessorKey: 'previousStatus', header: 'Previous' },
      { accessorKey: 'currentStatus', header: 'Current' },
    ],
  },
  actionRequiredChanges: {
    title: 'Action Required Changes',
    columns: [
      { accessorKey: 'changedAt', header: 'Changed', accessorFn: (row) => formatDateTime(row.changedAt) },
      { accessorKey: 'changeType', header: 'Change' },
      { accessorKey: 'programName', header: 'Program' },
      { accessorKey: 'bac', header: 'BAC' },
      { accessorKey: 'dealerName', header: 'Dealer' },
      { accessorKey: 'previousStatus', header: 'Previous' },
      { accessorKey: 'newStatus', header: 'New' },
    ],
  },
  vmtPushes: {
    title: 'VMT Pushes',
    columns: [
      { accessorKey: 'createdAt', header: 'Created', accessorFn: (row) => formatDateTime(row.createdAt) },
      { accessorKey: 'status', header: 'Status' },
      { accessorKey: 'statusCode', header: 'Code' },
      { accessorKey: 'recordCount', header: 'Records' },
      { accessorKey: 'environment', header: 'Env' },
      { accessorKey: 'userEmail', header: 'User' },
    ],
  },
  dbErrorLogs: {
    title: 'DB Error logs',
    columns: [
      { accessorKey: 'createdAt', header: 'Timestamp', accessorFn: (row) => formatDateTime(row.createdAt) },
      { accessorKey: 'level', header: 'Level' },
      { accessorKey: 'source', header: 'Source' },
      { accessorKey: 'event', header: 'Event' },
      { accessorKey: 'summary', header: 'Summary' },
      { accessorKey: 'message', header: 'Message' },
      { accessorKey: 'requestId', header: 'Request ID' },
      { accessorKey: 'context', header: 'Context' },
      { accessorKey: 'stack', header: 'Stack Trace' },
    ],
  },
  autoStatusUpdates: {
    title: 'Auto Status Updates',
    columns: [
      { accessorKey: 'changedAt', header: 'Changed', accessorFn: (row) => formatDateTime(row.changedAt) },
      { accessorKey: 'bac', header: 'BAC' },
      { accessorKey: 'salesforceAccountName', header: 'Salesforce Account' },
      { accessorKey: 'orderItemId', header: 'Order Item ID' },
      { accessorKey: 'programName', header: 'Program' },
      { accessorKey: 'dpePreviousStatus', header: 'DPE Previous' },
      { accessorKey: 'dpeNewStatus', header: 'DPE New' },
      { accessorKey: 'diStatus', header: 'DI Status' },
      { accessorKey: 'diEffectiveDate', header: 'DI Effective' },
      { accessorKey: 'diServiceStartDate', header: 'DI Service Start' },
      { accessorKey: 'dpeIsBilling', header: 'DPE Is Billing' },
      { accessorKey: 'syncStatus', header: 'Sync Status' },
      { accessorKey: 'failureReason', header: 'Failure Reason' },
      { accessorKey: 'isManual', header: 'Manual' },
    ],
  },
};

const DailyDigestSectionTable = ({
  section,
  rows,
  rowCount,
  isLoading,
  pagination,
  onPaginationChange,
  onInspectSend,
  onRetrySend,
}) => {
  const config = SECTION_CONFIG[section] || null;

  const columns = useMemo(() => {
    if (!config?.columns) return [];
    if (section !== 'triggerExecutions') return config.columns;
    if (!onInspectSend && !onRetrySend) return config.columns;
    return [
      ...config.columns,
      {
        id: 'actions',
        header: '',
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            {onInspectSend && (
              <IconButton
                size="small"
                aria-label="Inspect send"
                onClick={() => onInspectSend(row.original)}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            )}
            {onRetrySend && (
              <IconButton
                size="small"
                aria-label="Retry send"
                onClick={() => onRetrySend(row.original)}
                disabled={String(row.original.status || '').toLowerCase() !== 'failed'}
              >
                <RefreshIcon fontSize="inherit" />
              </IconButton>
            )}
          </Stack>
        ),
      },
    ];
  }, [config, onInspectSend, onRetrySend, section]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    rowCount,
    manualPagination: true,
    state: {
      isLoading,
      pagination,
    },
    onPaginationChange,
    localization: MRT_Localization_EN,
    muiTablePaperProps: {
      variant: 'outlined',
      sx: { borderRadius: 3 },
    },
  });

  return <MaterialReactTable table={table} />;
};

export { DailyDigestSectionTable, SECTION_CONFIG };
